// @flow

import * as React from 'react';
import { padding } from 'polished';
import { cond, constant, find, flow, get } from 'lodash/fp';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import { FRIEND_GROUP_ID } from 'app/constants/friendConstants';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import t from 'app/utils/translate';
import Responsive from 'app/components/responsive/Responsive';
import FilterButton from '../commons/FilterButton';
import SearchBar from '../commons/SearchBar';
import LeaderBoardFilterBarContent from './LeaderBoardFilterBarContent';
import FiltersToggleButton from '../commons/FiltersToggleButton';
import { IN_PROGRESS } from '../../constants/challengeStatusTypes';

type Props = {
  racingType?: string,
  challengeId?: string,
  countries: Array<Object>,
  filteredCountryId?: number,
  filteredGroupId?: number | string,
  friendGroups?: Array<FriendGroup>,
  isGroupsInitialised: boolean,
  isMultiEntry: boolean,
  isOpen: boolean,
  isRacing: boolean,
  isSignedIn: boolean,
  myEntriesOnly: boolean,
  onCountryChange: (?number) => void,
  onEntriesChange: () => void,
  onFilterClick: () => void,
  onGroupChange: (?number) => void,
  onReset: () => void,
  onSearchChange: (searchTerm?: string) => void,
  onSearchClear: () => void,
  onSelectEntity: (entity: Object) => void,
  players: Array<Object>,
  searchTerm?: string,
  selectedPlayers?: Array<string>,
  selectedTeams?: Array<string>
};

const findNameById = id => flow(find({ id }), get('name'));

const friendGroup: FriendGroup = {
  count: 0,
  id: FRIEND_GROUP_ID,
  members: []
};

const Wrapper = glamorous.div(({ theme }) => ({
  backgroundColor: theme.color.basis6,
  color: theme.color.basis1,
  fontSize: theme.fontSize.xsmall,
  marginTop: theme.spacing.small,
  [theme.mediaQuery.desktopAndUp]: {
    marginTop: 0
  }
}));

const Panel = glamorous.div({
  alignItems: 'stretch',
  display: 'flex',
  justifyContent: 'flex-end',
  textTransform: 'uppercase'
});

const LeaderBoardFilterBarContentWrapper = glamorous.div(({ theme }) => ({
  ...padding(0, theme.spacing.small)
}));

const StyledFilterButton = glamorous(FilterButton)({
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  margin: '0.25rem 0.5rem'
});

class LeaderBoardFilterBar extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    racingType: undefined,
    challengeId: undefined,
    filteredCountryId: undefined,
    filteredGroupId: undefined,
    friendGroups: [],
    searchTerm: undefined,
    selectedPlayers: [],
    selectedTeams: []
  };

  render() {
    const {
      challengeId,
      countries,
      filteredCountryId,
      filteredGroupId,
      friendGroups = [],
      isOpen,
      isGroupsInitialised,
      isMultiEntry,
      isRacing,
      racingType,
      isSignedIn,
      myEntriesOnly,
      onCountryChange,
      onEntriesChange,
      onSearchChange,
      onSearchClear,
      onFilterClick,
      onGroupChange,
      onReset,
      players,
      onSelectEntity,
      searchTerm,
      selectedPlayers,
      selectedTeams
    } = this.props;

    const currentFilter = cond([
      [constant(filteredCountryId), () => findNameById(filteredCountryId)(countries)]
    ])();

    return (
      <Wrapper>
        <Panel>
          <SearchBar
            value={searchTerm}
            onSearch={onSearchChange}
            placeholder="Search for an entrant"
            autoFocus={false}
            onClear={onSearchClear}
            scheme="dark"
          />
          {isMultiEntry && (
            <StyledFilterButton
              onClick={onEntriesChange}
              selected={myEntriesOnly}
              scheme="light"
              style={{ marginRight: '0.375em' }}
            >
              {t('My Entries')}
            </StyledFilterButton>
          )}
          <StyledFilterButton
            key={friendGroup.id}
            id={friendGroup.id}
            onClick={onGroupChange}
            selected={filteredGroupId === friendGroup.id}
            scheme="light"
          >
            {t('My Friends')}
          </StyledFilterButton>
          <FiltersToggleButton
            maxWidth={80}
            isFiltered={!!currentFilter || selectedPlayers.length > 0 || selectedTeams.length > 0}
            isOpen={isOpen}
            onClick={onFilterClick}
            onClear={onReset}
            text="Filters"
            scheme="light"
          />
        </Panel>
        <Responsive desktop>
          {isDesktop => (
            <AnimatedGroup
              onEnter={isDesktop && Enter.grow(0.5)}
              onLeave={isDesktop && Leave.shrink(0.5)}
            >
              {isOpen && (
                <LeaderBoardFilterBarContentWrapper>
                  <LeaderBoardFilterBarContent
                    key="filters"
                    challengeId={challengeId}
                    countries={countries}
                    filteredCountryId={filteredCountryId}
                    filteredGroupId={filteredGroupId}
                    friendGroups={friendGroups}
                    isLoading={isSignedIn && !isGroupsInitialised}
                    isRacing={isRacing}
                    racingType={racingType}
                    myEntriesOnly={myEntriesOnly}
                    onCountryChange={onCountryChange}
                    onEntriesChange={onEntriesChange}
                    onGroupChange={onGroupChange}
                    onSelectEntity={onSelectEntity}
                    players={players.filter(p => p.statusId >= IN_PROGRESS)}
                    showMyEntries={isSignedIn}
                    selectedPlayers={selectedPlayers}
                    selectedTeams={selectedTeams}
                    scheme="light"
                  />
                </LeaderBoardFilterBarContentWrapper>
              )}
            </AnimatedGroup>
          )}
        </Responsive>
      </Wrapper>
    );
  }
}

const connector = connect(
  state => ({
    challengeId: state.challenge.id,
    racingType: state.challenge.racingType,
    countries: state.challenge.countries,
    friendGroups: state.friendGroups.hasInitialised
      ? [friendGroup, ...Object.values(state.friendGroups.groups).reverse()]
      : [],
    isGroupsInitialised: state.friendGroups.hasInitialised,
    isRacing: state.challenge.isRacing,
    isMultiEntry: state.challenge.maxEntriesPerPerson > 1,
    isSignedIn: state.user.isSignedIn
  }),
  null
);

export default connector(LeaderBoardFilterBar);
